import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { CurrencyExchangeRateService } from '@app/v2/services/currency-exchange-rate/currency-exchange-rate.service';
import { ReimbursementRequestService } from '@app/v2/services/reimbursement-request/reimbursement-request.service';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import { Chip } from '@medpacesoftwaredevelopment/designsystem';
import { Expense } from '@models/request';
import { CurrencyStateService } from '@services/state-management/currency-state.service';
import { PatientStateService } from '@services/state-management/patient-state.service';
import { SiteStateService } from '@services/state-management/site-state.service';
import { StudyStateService } from '@services/state-management/study-state.service';
import { UserStateService } from '@services/state-management/user-state.service';
import { combineLatest, debounceTime, filter, map, merge, of, shareReplay, skip, switchMap, tap } from 'rxjs';
import { BaseExpenseFormGroup } from './base-expense-edit.form';
import { ExpenseFormGroup } from './expense-edit/expense-edit.form';

@Component({
    template: '',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class BaseExpenseComponent<TFormGroup extends BaseExpenseFormGroup> {
    index = input.required<number>();
    index$ = toObservable(this.index);
    expenseFormGroup = input.required<ExpenseFormGroup>();
    expenseFormGroup$ = toObservable(this.expenseFormGroup);
    formGrp = input.required<TFormGroup>();
    formGrp$ = toObservable(this.formGrp);

    reimbursementRequestService = inject(ReimbursementRequestService);
    requestMaybe$ = this.reimbursementRequestService.state$;
    requestNotNull$ = this.requestMaybe$.pipe(filter(Boolean));
    expense = input<Expense | undefined>(undefined);
    expenseMaybe$ = toObservable(this.expense);
    expenseNotNull$ = this.expenseMaybe$.pipe(filter(Boolean));

    userStateService = inject(UserStateService);
    userService = inject(UserServiceV2);
    currencyStateService = inject(CurrencyStateService);
    studyStateService = inject(StudyStateService);
    siteStateService = inject(SiteStateService);
    patientStateService = inject(PatientStateService);
    private readonly currencyExchangeRateService = inject(CurrencyExchangeRateService);

    patient$ = this.patientStateService.getPatient();
    patient = toSignal(this.patient$);
    elevatedAccess = toSignal(this.userService.permissions.request.access.elevated$);
    currencyOptions$ = this.currencyStateService.getCurrenciesAsMdsOptions();
    push(expense: Expense): Expense {
        const value = this.formGrp().getRawValue();
        const amounts = this.chipAmountsSignal();
        const currency = this.currencySignal().isocode.toUpperCase();
        const patientCurrency = this.patientPreferredCurrency();

        expense.amount =
            value.requestedAmountInLocalCurrency == null || value.requestedAmountInLocalCurrency === ''
                ? null
                : +value.requestedAmountInLocalCurrency;
        expense.authorizedAmount =
            value.authorizedAmountInLocalCurrency == null || value.authorizedAmountInLocalCurrency === ''
                ? null
                : +value.authorizedAmountInLocalCurrency;
        expense.authorizedAmountUsd =
            currency === 'USD'
                ? value.authorizedAmountInLocalCurrency == null || value.authorizedAmountInLocalCurrency === ''
                    ? null
                    : +value.authorizedAmountInLocalCurrency
                : (amounts?.usd ?? null);

        expense.authorizedAmountPatientsCurrency =
            currency === 'USD' && patientCurrency == currency
                ? value.authorizedAmountInLocalCurrency == null || value.authorizedAmountInLocalCurrency === ''
                    ? null
                    : +value.authorizedAmountInLocalCurrency
                : (amounts?.patientsCurrency ?? null);

        expense.exchangeRateDate = amounts?.exchangeRateDate ?? null;

        return expense;
    }
    currency$ = this.expenseFormGroup$.pipe(switchMap((formGrp) => formGrp.controls.currency.valueChangesReplay));

    isCurrencyUSD$ = this.currency$.pipe(
        map((currency) => {
            if (!currency) return null;
            return currency?.isocode.toLowerCase() === 'usd';
        })
    );

    requestedLabel$ = this.currency$.pipe(
        map((currency) => {
            if (currency) return `Requested (${currency.isocode})`;
            else return 'Requested';
        })
    );
    authorizedLabel$ = this.currency$.pipe(
        map((currency) => {
            if (currency) return `Authorized (${currency.isocode})`;
            else return 'Authorized';
        })
    );

    authorized$ = this.formGrp$.pipe(
        switchMap((formGrp) =>
            formGrp.controls.authorizedAmountInLocalCurrency.valueChangesReplay.pipe(
                map((valueString) => {
                    if (valueString == null || valueString === '') return null;
                    const parsed = Number(valueString);
                    return isNaN(parsed) ? null : parsed;
                }),
                filter((authorized) => authorized !== null)
            )
        )
    );

    patchFormGroup(expense: Expense) {
        this.formGrp().patchValue(
            {
                requestedAmountInLocalCurrency: expense.amount?.toFixed(2) ?? null,
            },
            { emitEvent: true }
        );

        if (this.elevatedAccess()) {
            let authorizedAmount = expense.authorizedAmount;
            if (authorizedAmount === null || authorizedAmount === undefined) authorizedAmount = expense.amount; // if authorized was not yet filled, initialize it with requestedAmount, for convenience only
            this.formGrp().patchValue(
                {
                    authorizedAmountInLocalCurrency: authorizedAmount?.toFixed(2) ?? null,
                },
                { emitEvent: true }
            );
        }
    }

    isInProcessOrHigherAndHasElevatedAccess$ = combineLatest({
        elevatedAccess: this.userService.permissions.request.access.elevated$,
        isInProcessOrHigher: this.reimbursementRequestService.isInProcessOrHigher$,
    }).pipe(
        map(({ elevatedAccess, isInProcessOrHigher }) => {
            return elevatedAccess && isInProcessOrHigher;
        }),
        shareReplay(1)
    );

    private readonly patientPreferredCurrency = computed(
        () => this.patient()?.preferredCurrency?.toUpperCase() ?? null
    );
    private readonly currencySignal = toSignal(this.currency$);
    private readonly patientPreferredCurrency$ = toObservable(this.patientPreferredCurrency);

    /**
     * Merges two phases:
     *  Phase 1 — initial$: emits the expense's stored converted amounts immediately on load.
     *  Phase 2 — recalculate$: fires only when the user changes authorizedAmountInLocalCurrency
     *             or receiptDate (uses `valueChanges`, not `valueChangesReplay`, as the trigger
     *             so the initial patch from patchFormGroup does NOT trigger a recalculation).
     */
    private readonly chipAmounts$ = combineLatest({
        expense: this.expenseMaybe$,
        formGrp: this.formGrp$,
        expenseFormGroup: this.expenseFormGroup$,
    }).pipe(
        switchMap(({ expense, formGrp, expenseFormGroup }) => {
            const initial$ = of({
                patientsCurrency: expense?.authorizedAmountPatientsCurrency ?? null,
                usd: expense?.authorizedAmountUsd ?? null,
                exchangeRateDate: expense?.exchangeRateDate ?? null,
            });

            const hasStoredValues =
                expense?.authorizedAmountPatientsCurrency != null || expense?.authorizedAmountUsd != null;

            const recalculate$ = combineLatest({
                // valueChanges (not valueChangesReplay) as trigger — only fires on actual user edits
                _trigger: merge(
                    formGrp.controls.authorizedAmountInLocalCurrency.valueChanges,
                    expenseFormGroup.controls.receiptDate.valueChanges,
                    this.currency$.pipe(skip(1))
                ),
                // valueChangesReplay to read the current value at the time of trigger
                authorizedStr: formGrp.controls.authorizedAmountInLocalCurrency.valueChangesReplay,
                date: expenseFormGroup.controls.receiptDate.valueChangesReplay,
                currency: this.currency$,
                patientCurrency: this.patientPreferredCurrency$,
                isInProcessOrHigherAndHasElevatedAccess: this.isInProcessOrHigherAndHasElevatedAccess$,
            }).pipe(
                debounceTime(300),
                skip(hasStoredValues ? 1 : 0),
                switchMap(
                    ({ authorizedStr, date, currency, patientCurrency, isInProcessOrHigherAndHasElevatedAccess }) => {
                        const authorized = authorizedStr ? parseFloat(authorizedStr) : null;
                        if (
                            !authorized ||
                            isNaN(authorized) ||
                            !date ||
                            !currency ||
                            !isInProcessOrHigherAndHasElevatedAccess
                        ) {
                            return of({
                                patientsCurrency: null as number | null,
                                usd: null as number | null,
                                exchangeRateDate: null as string | null,
                            });
                        }

                        return this.currencyExchangeRateService.convertAmount(
                            authorized,
                            currency.isocode.toUpperCase(),
                            date,
                            patientCurrency
                        );
                    }
                )
            );

            return merge(initial$, recalculate$);
        })
    );

    readonly chipAmountsSignal = toSignal(this.chipAmounts$);

    authorizedAmountPatientsCurrencyChip = computed((): Chip[] | null => {
        const patientCurrency = this.patientPreferredCurrency();
        const currency = this.currencySignal();
        if (!patientCurrency || !currency || patientCurrency === 'USD') return null;

        const expenseCurrency = currency.isocode.toUpperCase();
        if (expenseCurrency === patientCurrency) return null;

        const amounts = this.chipAmountsSignal();
        if (amounts?.patientsCurrency == null) return null;

        const value = `${amounts.patientsCurrency.toFixed(2)} ${patientCurrency} · patient`;
        return [{ value, ariaLabel: value, removable: false }];
    });

    authorizedAmountUSDChip = computed((): Chip[] | null => {
        const currency = this.currencySignal();
        if (!currency || currency.isocode.toUpperCase() === 'USD') return null;

        const amounts = this.chipAmountsSignal();
        if (amounts?.usd == null) return null;

        const value = `${amounts.usd.toFixed(2)} USD · reporting`;
        return [{ value, ariaLabel: value, removable: false }];
    });
    switchAuthorized$ = combineLatest({
        isInProcessOrHigherAndHasElevatedAccess: this.isInProcessOrHigherAndHasElevatedAccess$,
        formGrp: this.formGrp$,
    })
        .pipe(
            tap(({ isInProcessOrHigherAndHasElevatedAccess, formGrp }) => {
                if (isInProcessOrHigherAndHasElevatedAccess) {
                    formGrp.controls.authorizedAmountInLocalCurrency.enable({ force: true });
                } else {
                    formGrp.controls.authorizedAmountInLocalCurrency.disable({ persistent: true });
                }
            }),
            takeUntilDestroyed()
        )
        .subscribe();
}
